/**
 * Hook for fetching pricing data for fraud category billing codes.
 *
 * Phase 1: State hospital overview (runs on page load)
 * Phase 2: Category detail — national benchmarks + hospital-level analytics (lazy)
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { batchedIn } from '@/lib/batchedQuery';
import {
  FraudCategory,
  getCategoryCodesByType,
  getRiskLevel,
  type RiskLevel,
  type CodeType,
} from '@/config/fraudCategories';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface StateHospitalSummary {
  id: string;
  name: string;
  city: string;
  state: string;
  avg_gross_markup_percentage: number | null;
  shame_score: number | null;
  rank_position: number | null;
}

export interface NationalBenchmark {
  code: string;
  codeType: CodeType;
  description: string;
  nationalAvg: number | null;
  /** National median gross charge — the site's headline benchmark (the avg is the secondary figure). */
  nationalMedian: number | null;
  totalHospitals: number;
}

export interface HospitalCodePricing {
  hospitalId: string;
  procedureCode: string;
  procedureType: string;
  hospitalGrossCharge: number;
  nationalAvgGross: number;
  grossMarkupPercentage: number;
  procedureDescription: string;
  inpatientOutpatient: string;
}

export interface HospitalOutlier {
  hospitalId: string;
  hospitalName: string;
  city: string;
  codeCount: number;
  avgMarkup: number;
  maxMarkup: number;
  riskLevel: RiskLevel;
  codePricing: HospitalCodePricing[];
}

/* ------------------------------------------------------------------ */
/*  Phase 1: State hospital list + ranking summary                    */
/* ------------------------------------------------------------------ */

export function useStateHospitals(state: string) {
  return useQuery({
    queryKey: ['fraud-watchdog-hospitals', state],
    queryFn: async () => {
      // Get all hospitals in state
      const { data: hospitals, error: hospError } = await supabase
        .from('hospitals')
        .select('id, name, city, state')
        .eq('state', state);

      if (hospError) throw hospError;
      if (!hospitals?.length) return [];

      const hospitalIds = hospitals.map((h: any) => h.id);

      // Get ranking summaries for these hospitals
      const { data: rankings, error: rankError } = await batchedIn<any>(
        'hospital_ranking_summary',
        'hospital_id',
        hospitalIds,
        'hospital_id, avg_gross_markup_percentage, shame_score, rank_position'
      );

      if (rankError) throw rankError;

      const rankingMap = new Map(
        (rankings || []).map((r: any) => [r.hospital_id, r])
      );

      return hospitals.map((h: any) => {
        const r = rankingMap.get(h.id);
        return {
          id: h.id,
          name: h.name,
          city: h.city,
          state: h.state,
          avg_gross_markup_percentage: r?.avg_gross_markup_percentage ?? null,
          shame_score: r?.shame_score ?? null,
          rank_position: r?.rank_position ?? null,
        } as StateHospitalSummary;
      });
    },
    staleTime: Infinity,
  });
}

/* ------------------------------------------------------------------ */
/*  Phase 2: Category detail — national benchmarks + hospital pricing */
/* ------------------------------------------------------------------ */

/**
 * Maps code type to the analytics table procedure_type value and pricing table.
 */
const CODE_TYPE_MAP: Record<CodeType, { procedureType: string; pricingTable: string; codeColumn: string; avgColumn: string; medianColumn: string }> = {
  CPT:   { procedureType: 'cpt',     pricingTable: 'cpt_pricing_averages',     codeColumn: 'cpt_code',     avgColumn: 'avg_gross_charge', medianColumn: 'median_gross_charge' },
  HCPCS: { procedureType: 'hcpcs',   pricingTable: 'hcpcs_pricing_averages',   codeColumn: 'hcpcs_code',   avgColumn: 'avg_gross_charge', medianColumn: 'median_gross_charge' },
  DRG:   { procedureType: 'drg',     pricingTable: 'drg_pricing_averages',     codeColumn: 'drg_code',     avgColumn: 'avg_gross_charge', medianColumn: 'median_gross_charge' },
  REV:   { procedureType: 'revenue', pricingTable: 'revenue_pricing_averages', codeColumn: 'revenue_code', avgColumn: 'avg_charge', medianColumn: 'median_charge' },
};

export function useCategoryPricing(
  category: FraudCategory | null,
  hospitalIds: string[]
) {
  return useQuery({
    queryKey: ['fraud-category-pricing', category?.id, hospitalIds.length],
    queryFn: async () => {
      if (!category || !hospitalIds.length) {
        return { benchmarks: [], hospitalOutliers: [] };
      }

      const codesByType = getCategoryCodesByType(category);
      const benchmarks: NationalBenchmark[] = [];
      const allHospitalPricing: HospitalCodePricing[] = [];

      // For each code type that has codes, fetch national benchmarks and hospital analytics
      const fetchPromises = (Object.keys(codesByType) as CodeType[])
        .filter((ct) => codesByType[ct].length > 0)
        .map(async (codeType) => {
          const codes = codesByType[codeType];
          const { procedureType, pricingTable, codeColumn, avgColumn, medianColumn } = CODE_TYPE_MAP[codeType];

          // Fetch national benchmarks from pricing averages table
          // Plain string: supabase's select-string type parser can't follow a
          // three-placeholder template, and the table is already `as any`.
          const benchmarkSelect: string = `${codeColumn}, ${avgColumn}, ${medianColumn}, total_hospitals`;
          let benchmarkQuery = supabase
            .from(pricingTable as any)
            .select(benchmarkSelect as '*')
            .in(codeColumn, codeType === 'DRG' ? codes.map(c => `MS-${c}`) : codes);

          // For CPT/HCPCS, filter to all_settings and most representative row
          if (codeType === 'CPT' || codeType === 'HCPCS') {
            benchmarkQuery = benchmarkQuery.eq('setting_type', 'all_settings');
          }

          // For REV codes, filter to 'other' normalized unit for per-encounter rate
          if (codeType === 'REV') {
            benchmarkQuery = benchmarkQuery.eq('normalized_unit', 'other');
          }

          const { data: benchmarkData } = await benchmarkQuery;

          if (benchmarkData) {
            // Deduplicate — take the row with the most hospitals per code
            const bestByCode = new Map<string, any>();
            for (const row of benchmarkData) {
              const rawCode = row[codeColumn];
              const code = codeType === 'DRG' ? rawCode.replace('MS-', '') : rawCode;
              const existing = bestByCode.get(code);
              if (!existing || (row.total_hospitals || 0) > (existing.total_hospitals || 0)) {
                bestByCode.set(code, row);
              }
            }

            for (const [code, row] of bestByCode) {
              const catCode = category.codes.find(
                (c) => c.code === code && c.codeType === codeType
              );
              benchmarks.push({
                code,
                codeType,
                description: catCode?.description || '',
                nationalAvg: row[avgColumn],
                nationalMedian: row[medianColumn],
                totalHospitals: row.total_hospitals || 0,
              });
            }
          }

          // Fetch hospital-level analytics for these codes + these hospital IDs
          // Use batchedIn on hospital_id, then filter by code
          const analyticsSelect = 'hospital_id, procedure_code, procedure_type, hospital_gross_charge, national_avg_gross, gross_markup_percentage, procedure_description, inpatient_outpatient';

          // We need to query by hospital_id IN (...) AND procedure_code IN (...) AND procedure_type = ...
          // batchedIn only supports one IN clause, so we batch on hospital_id and filter codes client-side
          // But hospital_procedure_analytics is 315M rows — we need server-side filtering on code too
          // Strategy: batch on hospital_id, add .in() for codes, and .eq() for procedure_type

          const analyticsCodeValues = codeType === 'DRG' ? codes.map(c => `MS-${c}`) : codes;
          const BATCH = 20;
          const chunks: string[][] = [];
          for (let i = 0; i < hospitalIds.length; i += BATCH) {
            chunks.push(hospitalIds.slice(i, i + BATCH));
          }

          const chunkResults = await Promise.all(
            chunks.map((chunk) =>
              supabase
                .from('hospital_procedure_analytics')
                .select(analyticsSelect)
                .in('hospital_id', chunk)
                .in('procedure_code', analyticsCodeValues)
                .eq('procedure_type', procedureType)
                .not('gross_markup_percentage', 'is', null)
                .gt('national_avg_gross', 0)
            )
          );

          for (const result of chunkResults) {
            if (result.data) {
              for (const row of result.data) {
                allHospitalPricing.push({
                  hospitalId: row.hospital_id,
                  procedureCode: codeType === 'DRG' ? row.procedure_code.replace('MS-', '') : row.procedure_code,
                  procedureType: row.procedure_type,
                  hospitalGrossCharge: row.hospital_gross_charge,
                  nationalAvgGross: row.national_avg_gross,
                  grossMarkupPercentage: row.gross_markup_percentage,
                  procedureDescription: row.procedure_description,
                  inpatientOutpatient: row.inpatient_outpatient || '',
                });
              }
            }
          }
        });

      await Promise.all(fetchPromises);

      // Sort benchmarks by code type then code
      benchmarks.sort((a, b) => {
        if (a.codeType !== b.codeType) return a.codeType.localeCompare(b.codeType);
        return a.code.localeCompare(b.code);
      });

      // Aggregate hospital-level pricing into outlier summaries
      const hospitalMap = new Map<string, HospitalCodePricing[]>();
      for (const hp of allHospitalPricing) {
        const existing = hospitalMap.get(hp.hospitalId) || [];
        existing.push(hp);
        hospitalMap.set(hp.hospitalId, existing);
      }

      return { benchmarks, hospitalPricingMap: hospitalMap };
    },
    enabled: !!category && hospitalIds.length > 0,
    staleTime: Infinity,
  });
}

/**
 * Compute hospital outliers from raw pricing data + hospital name lookup.
 */
export function computeOutliers(
  hospitalPricingMap: Map<string, HospitalCodePricing[]>,
  hospitals: StateHospitalSummary[]
): HospitalOutlier[] {
  const nameMap = new Map(hospitals.map((h) => [h.id, h]));
  const outliers: HospitalOutlier[] = [];

  for (const [hospitalId, pricing] of hospitalPricingMap) {
    const hosp = nameMap.get(hospitalId);
    if (!hosp || pricing.length === 0) continue;

    const markups = pricing.map((p) => p.grossMarkupPercentage);
    const avgMarkup = markups.reduce((a, b) => a + b, 0) / markups.length;
    const maxMarkup = Math.max(...markups);

    outliers.push({
      hospitalId,
      hospitalName: hosp.name,
      city: hosp.city,
      codeCount: pricing.length,
      avgMarkup,
      maxMarkup,
      riskLevel: getRiskLevel(avgMarkup),
      codePricing: pricing,
    });
  }

  // Sort by average markup descending (worst first)
  outliers.sort((a, b) => b.avgMarkup - a.avgMarkup);
  return outliers;
}
